import React, { useState } from 'react';
import { Modal, Button, Input } from '@/components/UI';
import { useSitesStore } from '@/stores/sites';
import { api } from '@/services/api';
import type { SiteWithSnippet } from '@/types';

interface AddSiteWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  organizationId: string;
}

type Step = 'details' | 'code' | 'verify';

export function AddSiteWizard({ isOpen, onClose, onSuccess, organizationId }: AddSiteWizardProps) {
  const { createSite } = useSitesStore();

  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdSite, setCreatedSite] = useState<SiteWithSnippet | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'success' | 'failed'>('idle');
  const [copied, setCopied] = useState(false);

  const handleClose = (success = false) => {
    // Reset state
    setStep('details');
    setName('');
    setDomain('');
    setError('');
    setCreatedSite(null);
    setVerificationStatus('idle');
    setCopied(false);
    if (success && onSuccess) {
      onSuccess();
    }
    onClose();
  };

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !domain.trim()) {
      setError('Name and domain are required');
      return;
    }

    setIsSubmitting(true);
    try {
      const site = await createSite(organizationId, { name, domain });
      setCreatedSite(site);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyCode = async () => {
    if (createdSite?.tracking_snippet) {
      await navigator.clipboard.writeText(createdSite.tracking_snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleVerify = async () => {
    if (!createdSite) return;

    setIsVerifying(true);
    setVerificationStatus('idle');

    try {
      const response = await api.post<{ verified: boolean; message: string }>(
        `/api/sites/${createdSite.id}/verify-installation`
      );

      if (response.data.verified) {
        setVerificationStatus('success');
      } else {
        setVerificationStatus('failed');
        setError(response.data.message || 'Tracking code not detected');
      }
    } catch (err) {
      setVerificationStatus('failed');
      setError('Could not verify installation. Make sure your site is accessible.');
    } finally {
      setIsVerifying(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {(['details', 'code', 'verify'] as Step[]).map((s, i) => (
        <React.Fragment key={s}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === s
                ? 'bg-primary-600 text-white'
                : ['details'].includes(step) && s !== 'details'
                ? 'bg-gray-200 text-gray-500'
                : step === 'verify' || (step === 'code' && s === 'details')
                ? 'bg-primary-100 text-primary-600'
                : 'bg-gray-200 text-gray-500'
            }`}
          >
            {i + 1}
          </div>
          {i < 2 && (
            <div
              className={`w-12 h-0.5 ${
                (step === 'code' && s === 'details') || (step === 'verify' && s !== 'verify')
                  ? 'bg-primary-600'
                  : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderDetailsStep = () => (
    <form onSubmit={handleCreateSite} className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Add Your Site</h3>
        <p className="text-sm text-gray-500">Enter your website details</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <Input
        label="Site Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="My Awesome Site"
        required
      />

      <Input
        label="Domain"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        placeholder="example.com"
        helperText="Enter your domain without http:// or https://"
        required
      />

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={() => handleClose()}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isSubmitting}>
          Continue
        </Button>
      </div>
    </form>
  );

  const renderCodeStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Install Tracking Code</h3>
        <p className="text-sm text-gray-500">
          Add this code to the <code className="bg-gray-100 px-1 rounded">&lt;head&gt;</code> of your website
        </p>
      </div>

      <div>
        <div className="flex justify-end mb-2">
          <button
            onClick={handleCopyCode}
            className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded-md transition-colors"
          >
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
          <code>{createdSite?.tracking_snippet}</code>
        </pre>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Installation Tips</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Place this code in the <code className="bg-blue-100 px-1 rounded">&lt;head&gt;</code> section of every page</li>
          <li>• For React/Vue/Next.js, add it to your main HTML template</li>
          <li>• The script loads asynchronously and won't slow down your site</li>
        </ul>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Custom Events (Optional)</h4>
        <p className="text-sm text-gray-600 mb-2">Track custom events like signups, purchases, or button clicks:</p>
        <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
          <code>{`statsmatic('signup', {plan: 'pro'})
statsmatic('purchase', {amount: 49.99})`}</code>
        </pre>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="secondary" onClick={() => setStep('details')}>
          Back
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => handleClose(true)}>
            I'll verify later
          </Button>
          <Button onClick={() => setStep('verify')}>
            Verify Installation
          </Button>
        </div>
      </div>
    </div>
  );

  const renderVerifyStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Verify Installation</h3>
        <p className="text-sm text-gray-500">
          Let's make sure the tracking code is properly installed
        </p>
      </div>

      {verificationStatus === 'idle' && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          </div>
          <p className="text-gray-600 mb-6">
            Click the button below to check if the tracking code is installed on{' '}
            <strong>{createdSite?.domain}</strong>
          </p>
          <Button onClick={handleVerify} isLoading={isVerifying}>
            {isVerifying ? 'Checking...' : 'Check Installation'}
          </Button>
        </div>
      )}

      {verificationStatus === 'success' && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Successfully Verified!</h4>
          <p className="text-gray-600 mb-6">
            Your tracking code is properly installed. Analytics data will start appearing within a few minutes.
          </p>
          <Button onClick={() => handleClose(true)}>Done</Button>
        </div>
      )}

      {verificationStatus === 'failed' && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Not Detected Yet</h4>
          <p className="text-gray-600 mb-2">
            We couldn't find the tracking code on your site. This could mean:
          </p>
          <ul className="text-sm text-gray-500 mb-6 text-left max-w-sm mx-auto">
            <li>• The code hasn't been deployed yet</li>
            <li>• The code is on a different page</li>
            <li>• Your site isn't publicly accessible</li>
            <li>• There's a caching delay</li>
          </ul>
          <div className="flex justify-center gap-3">
            <Button variant="secondary" onClick={() => setStep('code')}>
              View Code Again
            </Button>
            <Button onClick={handleVerify} isLoading={isVerifying}>
              Try Again
            </Button>
          </div>
        </div>
      )}

      {verificationStatus === 'idle' && (
        <div className="flex justify-between pt-4">
          <Button variant="secondary" onClick={() => setStep('code')}>
            Back
          </Button>
          <Button variant="secondary" onClick={() => handleClose(true)}>
            Skip for Now
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="lg">
      {renderStepIndicator()}
      {step === 'details' && renderDetailsStep()}
      {step === 'code' && renderCodeStep()}
      {step === 'verify' && renderVerifyStep()}
    </Modal>
  );
}

export default AddSiteWizard;
